import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../config/api';

interface MovementPageProps {
  user: any;
  coachMode?: boolean;
  clientId?: number;
  clientName?: string;
}

// Activity type icons
const ACTIVITY_ICONS: Record<string, string> = {
  walk: '🚶',
  run: '🏃',
  gym: '🏋️',
  cycle: '🚴',
  swim: '🏊',
  hike: '🥾',
  yoga: '🧘',
  other: '📋',
};

// Rating display component
const RatingStars: React.FC<{ rating: number | null; max?: number }> = ({ rating, max = 5 }) => {
  if (rating === null) return <span className="text-gray-400">-</span>;
  return (
    <span className="text-yellow-500">
      {'★'.repeat(rating)}{'☆'.repeat(max - rating)}
    </span>
  );
};

// Format duration
const formatDuration = (seconds: number | null): string => {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

// Format date
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
};

export const MovementPage: React.FC<MovementPageProps> = ({
  user,
  coachMode,
  clientId,
  clientName
}) => {
  const [selectedTab, setSelectedTab] = useState<'overview' | 'workouts' | 'steps' | 'logs' | 'habits' | 'measurements'>('overview');
  const [timeframe, setTimeframe] = useState<number>(30);

  // Fetch movement profile/overview
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['client-movement-profile', clientId],
    queryFn: () => apiClient.getClientMovementProfile(clientId!),
    enabled: !!clientId && coachMode,
  });

  // Fetch assignments
  const { data: assignmentsData, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['client-movement-assignments', clientId, timeframe],
    queryFn: () => apiClient.getClientMovementAssignments(clientId!, { limit: 50 }),
    enabled: !!clientId && coachMode && (selectedTab === 'overview' || selectedTab === 'workouts'),
  });

  // Fetch completions
  const { data: completionsData, isLoading: completionsLoading } = useQuery({
    queryKey: ['client-movement-completions', clientId, timeframe],
    queryFn: () => apiClient.getClientMovementCompletions(clientId!, { limit: 50 }),
    enabled: !!clientId && coachMode && (selectedTab === 'overview' || selectedTab === 'workouts'),
  });

  // Fetch steps
  const { data: stepsData, isLoading: stepsLoading } = useQuery({
    queryKey: ['client-movement-steps', clientId, timeframe],
    queryFn: () => apiClient.getClientMovementSteps(clientId!, timeframe),
    enabled: !!clientId && coachMode && (selectedTab === 'overview' || selectedTab === 'steps'),
  });

  // Fetch movement logs
  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['client-movement-logs', clientId, timeframe],
    queryFn: () => apiClient.getClientMovementLogs(clientId!, timeframe),
    enabled: !!clientId && coachMode && (selectedTab === 'overview' || selectedTab === 'logs'),
  });

  // Fetch habits
  const { data: habitsData, isLoading: habitsLoading } = useQuery({
    queryKey: ['client-movement-habits', clientId],
    queryFn: () => apiClient.getClientMovementHabits(clientId!),
    enabled: !!clientId && coachMode && (selectedTab === 'overview' || selectedTab === 'habits'),
  });

  // Fetch measurements
  const { data: measurementsData, isLoading: measurementsLoading } = useQuery({
    queryKey: ['client-movement-measurements', clientId],
    queryFn: () => apiClient.getClientMovementMeasurements(clientId!),
    enabled: !!clientId && coachMode && (selectedTab === 'overview' || selectedTab === 'measurements'),
  });

  if (!clientId || !coachMode) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          <div className="text-4xl mb-4">🏃</div>
          <p>Select a client to view their movement data</p>
        </div>
      </div>
    );
  }

  const isLoading = profileLoading || assignmentsLoading || completionsLoading || stepsLoading || logsLoading || habitsLoading || measurementsLoading;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'workouts', label: 'Workouts', icon: '💪' },
    { id: 'steps', label: 'Steps', icon: '👟' },
    { id: 'logs', label: 'Activity Logs', icon: '📝' },
    { id: 'habits', label: 'Habits', icon: '✅' },
    { id: 'measurements', label: 'Measurements', icon: '📏' },
  ];

  const stats = profileData?.stats?.last30Days;

  return (
    <div className="p-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Movement Dashboard
        </h1>
        <p className="text-gray-600">
          Viewing {clientName || 'client'}'s exercise and movement data
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="flex border-b overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id as any)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                selectedTab === tab.id
                  ? 'border-b-2 border-green-500 text-green-600 bg-green-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Timeframe Selector */}
      {selectedTab !== 'habits' && selectedTab !== 'overview' && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-gray-600">Timeframe:</span>
          {[7, 14, 30, 60, 90].map((days) => (
            <button
              key={days}
              onClick={() => setTimeframe(days)}
              className={`px-3 py-1 text-sm rounded-full ${
                timeframe === days
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {days}d
            </button>
          ))}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
        </div>
      )}

      {/* Overview Tab */}
      {selectedTab === 'overview' && !isLoading && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Steps Summary */}
            <div className="bg-white rounded-lg shadow-sm p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500">Daily Steps (Avg)</h3>
                <span className="text-2xl">👟</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.steps?.avgSteps?.toLocaleString() || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Goal: {stats?.steps?.avgGoal?.toLocaleString() || 10000} |
                {' '}{stats?.steps?.daysLogged || 0} days tracked
              </p>
            </div>

            {/* Workouts Completed */}
            <div className="bg-white rounded-lg shadow-sm p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500">Workouts Completed</h3>
                <span className="text-2xl">💪</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.workouts?.completedWorkouts || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Avg Rating: {stats?.workouts?.avgRating?.toFixed(1) || '-'}/5
              </p>
            </div>

            {/* Assignment Completion */}
            <div className="bg-white rounded-lg shadow-sm p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500">Assignments</h3>
                <span className="text-2xl">📋</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.assignments?.completed || 0}/{stats?.assignments?.total || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {stats?.assignments?.completionRate || 0}% completion rate
              </p>
            </div>

            {/* Active Habits */}
            <div className="bg-white rounded-lg shadow-sm p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500">Active Habits</h3>
                <span className="text-2xl">✅</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.activeHabits || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Movement habits tracked
              </p>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Movement Logs */}
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Recent Activity Logs</h3>
              {logsData?.entries?.slice(0, 5).map((log) => (
                <div key={log.id} className="flex items-center justify-between py-2 border-b last:border-b-0">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">
                      {log.activityTypes?.[0] ? ACTIVITY_ICONS[log.activityTypes[0]] || '📋' : '👟'}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDate(log.date)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {log.activityTypes?.join(', ') || 'Steps only'}
                        {log.steps && ` • ${log.steps.toLocaleString()} steps`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {log.motivationRating && (
                      <p className="text-xs text-gray-500">
                        Motivation: <RatingStars rating={log.motivationRating} />
                      </p>
                    )}
                  </div>
                </div>
              )) || <p className="text-gray-500 text-sm">No activity logs yet</p>}
            </div>

            {/* Recent Workout Completions */}
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Recent Workout Completions</h3>
              {completionsData?.completions?.slice(0, 5).map((completion) => (
                <div key={completion.id} className="flex items-center justify-between py-2 border-b last:border-b-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {completion.workout?.title}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(completion.completedAt)} • {formatDuration(completion.durationSec)}
                    </p>
                  </div>
                  <div className="text-right">
                    <RatingStars rating={completion.feedbackRating} />
                  </div>
                </div>
              )) || <p className="text-gray-500 text-sm">No workout completions yet</p>}
            </div>
          </div>
        </div>
      )}

      {/* Workouts Tab */}
      {selectedTab === 'workouts' && !isLoading && (
        <div className="space-y-6">
          {/* Assignments */}
          <div className="bg-white rounded-lg shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Workout Assignments</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Date</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Workout</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Duration</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Status</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {assignmentsData?.assignments?.map((assignment) => (
                    <tr key={assignment.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">{formatDate(assignment.scheduledDate)}</td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{assignment.workout?.title}</p>
                          <p className="text-xs text-gray-500">{assignment.workout?.typeName || 'General'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">{formatDuration(assignment.workout?.estimatedTotalSec)}</td>
                      <td className="px-4 py-3">
                        {assignment.completed ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            Completed
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <RatingStars rating={assignment.feedbackRating} />
                      </td>
                    </tr>
                  )) || (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        No workout assignments found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Completions */}
          <div className="bg-white rounded-lg shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Workout Completions (Catalogue)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Date</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Workout</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Type</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Duration</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Rating</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {completionsData?.completions?.map((completion) => (
                    <tr key={completion.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">{formatDate(completion.completedAt)}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{completion.workout?.title}</td>
                      <td className="px-4 py-3">{completion.workout?.type?.name || 'General'}</td>
                      <td className="px-4 py-3">{formatDuration(completion.durationSec)}</td>
                      <td className="px-4 py-3"><RatingStars rating={completion.feedbackRating} /></td>
                      <td className="px-4 py-3 max-w-xs truncate text-gray-600">{completion.feedbackNotes || '-'}</td>
                    </tr>
                  )) || (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        No workout completions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Steps Tab */}
      {selectedTab === 'steps' && !isLoading && (
        <div className="space-y-6">
          {/* Steps Summary */}
          <div className="bg-white rounded-lg shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Steps Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{stepsData?.summary?.daysTracked || 0}</p>
                <p className="text-sm text-gray-500">Days Tracked</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{stepsData?.summary?.totalSteps?.toLocaleString() || 0}</p>
                <p className="text-sm text-gray-500">Total Steps</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{stepsData?.summary?.avgSteps?.toLocaleString() || 0}</p>
                <p className="text-sm text-gray-500">Daily Average</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{stepsData?.summary?.goalMetDays || 0}</p>
                <p className="text-sm text-gray-500">Goal Met Days</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">{stepsData?.summary?.goalMetRate || 0}%</p>
                <p className="text-sm text-gray-500">Goal Rate</p>
              </div>
            </div>
          </div>

          {/* Steps History */}
          <div className="bg-white rounded-lg shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Step History</h3>
            <div className="space-y-2">
              {stepsData?.entries?.map((entry) => {
                const percentage = Math.min(100, (entry.steps / entry.goal) * 100);
                const goalMet = entry.steps >= entry.goal;
                return (
                  <div key={entry.id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50">
                    <span className="text-sm font-medium text-gray-600 w-24">{formatDate(entry.date)}</span>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${goalMet ? 'bg-green-500' : 'bg-blue-500'}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                    <span className={`text-sm font-bold w-20 text-right ${goalMet ? 'text-green-600' : 'text-gray-700'}`}>
                      {entry.steps.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-400 w-16">/{entry.goal.toLocaleString()}</span>
                    {goalMet && <span className="text-green-500">✓</span>}
                  </div>
                );
              }) || <p className="text-gray-500 text-sm">No step data yet</p>}
            </div>
          </div>
        </div>
      )}

      {/* Activity Logs Tab */}
      {selectedTab === 'logs' && !isLoading && (
        <div className="space-y-6">
          {/* Logs Summary */}
          <div className="bg-white rounded-lg shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Activity Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{logsData?.summary?.totalLogs || 0}</p>
                <p className="text-sm text-gray-500">Total Logs</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{logsData?.summary?.activeDays || 0}</p>
                <p className="text-sm text-gray-500">Active Days</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{logsData?.summary?.avgMotivation?.toFixed(1) || '-'}</p>
                <p className="text-sm text-gray-500">Avg Motivation</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{logsData?.summary?.avgEnergy?.toFixed(1) || '-'}</p>
                <p className="text-sm text-gray-500">Avg Energy</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">{logsData?.summary?.avgPerformance?.toFixed(1) || '-'}</p>
                <p className="text-sm text-gray-500">Avg Performance</p>
              </div>
            </div>
          </div>

          {/* Activity Type Breakdown */}
          {logsData?.summary?.activityCounts && Object.keys(logsData.summary.activityCounts).length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Activity Breakdown</h3>
              <div className="flex flex-wrap gap-3">
                {Object.entries(logsData.summary.activityCounts).map(([activity, count]) => (
                  <div key={activity} className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full">
                    <span className="text-xl">{ACTIVITY_ICONS[activity] || '📋'}</span>
                    <span className="font-medium capitalize">{activity}</span>
                    <span className="text-sm text-gray-500">({count})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Logs List */}
          <div className="bg-white rounded-lg shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Activity Log History</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Date</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Activities</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Steps</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Distance</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Motivation</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Energy</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Performance</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {logsData?.entries?.map((log) => (
                    <tr key={log.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">{formatDate(log.date)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {log.activityTypes?.map((type, i) => (
                            <span key={i} title={type} className="text-xl">
                              {ACTIVITY_ICONS[type] || '📋'}
                            </span>
                          )) || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">{log.steps?.toLocaleString() || '-'}</td>
                      <td className="px-4 py-3">{log.distanceMiles ? `${log.distanceMiles} mi` : '-'}</td>
                      <td className="px-4 py-3"><RatingStars rating={log.motivationRating} /></td>
                      <td className="px-4 py-3"><RatingStars rating={log.energyRating} /></td>
                      <td className="px-4 py-3"><RatingStars rating={log.performanceRating} /></td>
                      <td className="px-4 py-3 max-w-xs truncate text-gray-600">{log.notes || '-'}</td>
                    </tr>
                  )) || (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        No activity logs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Habits Tab */}
      {selectedTab === 'habits' && !isLoading && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Active Movement Habits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {habitsData?.habits?.map((habit) => (
                <div key={habit.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-900">{habit.title}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      habit.completionRate >= 70
                        ? 'bg-green-100 text-green-700'
                        : habit.completionRate >= 40
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {habit.completionRate}% completion
                    </span>
                  </div>
                  {habit.description && (
                    <p className="text-sm text-gray-600 mb-3">{habit.description}</p>
                  )}
                  {/* Recent check-ins visualization */}
                  <div className="flex gap-1 mt-2">
                    {habit.checkins?.slice(0, 14).reverse().map((checkin, i) => (
                      <div
                        key={i}
                        className={`w-4 h-4 rounded-sm ${
                          checkin.status === 'done'
                            ? 'bg-green-500'
                            : checkin.status === 'skipped'
                            ? 'bg-yellow-500'
                            : 'bg-gray-300'
                        }`}
                        title={`${checkin.date}: ${checkin.status}`}
                      />
                    ))}
                  </div>
                </div>
              )) || <p className="text-gray-500 text-sm col-span-2">No active habits</p>}
            </div>
          </div>
        </div>
      )}

      {/* Measurements Tab */}
      {selectedTab === 'measurements' && !isLoading && (
        <div className="space-y-6">
          {/* Trends Summary */}
          {measurementsData?.trends && (
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Measurement Trends</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className={`text-2xl font-bold ${
                    measurementsData.trends.weightChange && measurementsData.trends.weightChange < 0
                      ? 'text-green-600'
                      : measurementsData.trends.weightChange && measurementsData.trends.weightChange > 0
                      ? 'text-red-600'
                      : 'text-gray-600'
                  }`}>
                    {measurementsData.trends.weightChange !== null
                      ? `${measurementsData.trends.weightChange > 0 ? '+' : ''}${measurementsData.trends.weightChange} kg`
                      : '-'}
                  </p>
                  <p className="text-sm text-gray-500">Weight Change</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className={`text-2xl font-bold ${
                    measurementsData.trends.waistChange && measurementsData.trends.waistChange < 0
                      ? 'text-green-600'
                      : measurementsData.trends.waistChange && measurementsData.trends.waistChange > 0
                      ? 'text-red-600'
                      : 'text-gray-600'
                  }`}>
                    {measurementsData.trends.waistChange !== null
                      ? `${measurementsData.trends.waistChange > 0 ? '+' : ''}${measurementsData.trends.waistChange} cm`
                      : '-'}
                  </p>
                  <p className="text-sm text-gray-500">Waist Change</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">
                    {measurementsData.trends.periodDays || 0}
                  </p>
                  <p className="text-sm text-gray-500">Days Tracked</p>
                </div>
              </div>
            </div>
          )}

          {/* Measurements History */}
          <div className="bg-white rounded-lg shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Measurement History</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Date</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Weight (kg)</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Waist (cm)</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {measurementsData?.measurements?.map((measurement) => (
                    <tr key={measurement.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">{formatDate(measurement.recordedAt)}</td>
                      <td className="px-4 py-3 font-medium">{measurement.weightKg || '-'}</td>
                      <td className="px-4 py-3">{measurement.waistCm || '-'}</td>
                      <td className="px-4 py-3 max-w-xs truncate text-gray-600">{measurement.notes || '-'}</td>
                    </tr>
                  )) || (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                        No measurements recorded yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MovementPage;
